import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} StudentResponseItem
 * @property {number} id - Student ID
 * @property {string} tenant_id - Tenant ID
 * @property {string|null} inst_id - Institution ID
 * @property {string} name - Student Name
 * @property {string} username - Student Username
 * @property {number} is_pswd_reset - Password Reset Status
 * @property {string} email - Student Email
 * @property {string|number} age - Student Age
 * @property {number} role_id - Role ID
 * @property {string} assigned_to - Assigned To Teacher or Admin Id
 * @property {string} createdBy - Created By Teacher or Admin Id
 * @property {string|null} class_id - Class ID
 * @property {string} status - Student Status
 * @property {string} created_at - Created At
 * @property {string} updated_at - Updated At
 * @property {string} student_code - Student Code
 */

/**
 * @typedef {object} PaginationMeta
 * @property {number} current_page - Current Page Number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last Page Number
 */

/**
 * @typedef {object} GetStudentParams
 * @property {number} [page]        - Page number for pagination
 * @property {number} [per_page]    - Number of items per page
 * @property {object} [filters]     - Filters in key:value format (key can only be name , email, username)
 * @property {string} [sort]        - Sorting (e.g. "name" for ascending or "-name" for descending)
 * @property {boolean} [unassigned_students]
 */

export const useGetStudentsByTeacher = () => {
  const [students, setStudents] = useState(
    /** @type {StudentResponseItem[] | null|undefined} */(undefined),
  );
  const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch students with pagination, filtering & sorting
   * @async
   * @param {GetStudentParams} params
   * @returns {Promise<void>}
   */
  const getStudentsByTeacher = async ({
    page = 1,
    per_page = 10,
    filters = {},
    sort = "",
    unassigned_students = false,
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      // --- Build Query Params ---
      const query = new URLSearchParams();

      // Pagination
      query.append("page", page);
      query.append("per_page", per_page);

      // Filters
      Object.keys(filters).forEach((field) => {
        if (filters[field] !== "" && filters[field] !== undefined) {
          query.append(`filter[${field}]`, filters[field]);
        }
      });

      // Sorting
      if (sort) query.append("sort", sort);

      if (unassigned_students) {
        query.append("with_scope", "unassigned_classrooms");
      }

      const url = `${TENANT_DOMAIN}/students?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setStudents(response.data.data || []);
      setMeta(response.data.meta || null);
    } catch (err) {
      setStudents(null);
      setMeta(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    students,
    meta,
    loading,
    error,
    getStudentsByTeacher,
  };
};
